import { getSortedHazardFeatures, MapboxSource, MapMode, useHazardsStore, useMapStore } from '@/features/hazards';
import { LngLatLike, Marker } from 'mapbox-gl';
import { h, render } from 'vue';
import UiMapClusterMarker from '../components/UiMap/UiMapClusterMarker/UiMapClusterMarker.vue';
import UiMapMarker from '../components/UiMap/UiMapMarker.vue';

import { generateChecksum } from '@/common';
import { Point } from 'geojson';

/**
 * Manages the markers on the map.
 * Clears all markers if the map mode is not {@link MapMode.Default}.
 * Renders two types of markers:
 *  1. {@link UiMapClusterMarker} for clusters
 *  2. {@link UiMapMarker} for individual hazards
 *
 * Uses `markers` and `clusterMarkersOnScreen` to optimise the rendering by reusing
 * existing markers.
 *
 * Derived from https://docs.mapbox.com/mapbox-gl-js/example/cluster-html/
 */
export const updateMarkers = async () => {
  const map = useMapStore();
  const hazards = useHazardsStore();
  const mapbox = map.mapbox!;

  /// Clear all markers if the map mode is not Default.
  if (hazards.mapMode !== MapMode.Default) {
    for (const id in map.clusterMarkersOnScreen) {
      map.clusterMarkersOnScreen[id].remove();
      delete map.clusterMarkersOnScreen[id];
    }

    for (const id in map.clusterMarkers) {
      const element = map.clusterMarkers[id].getElement();

      if (element) render(null, element); // unmount

      delete map.clusterMarkers[id];
    }
    map.clustersToClean.clear();
    return;
  }

  const newMarkers: Record<string, Marker> = {};
  const source = mapbox.getSource(MapboxSource.hazardsMarkersSource);
  if (!source || source.type != 'geojson') return;

  const clusterFeatures = mapbox.querySourceFeatures(MapboxSource.hazardsMarkersSource);

  // for every cluster on the screen, create an HTML marker for it (if we didn't yet),
  // and add it to the map if it's not there already
  for (const feature of clusterFeatures) {
    const coords = (feature.geometry as Point).coordinates as LngLatLike;
    const props = feature.properties!;

    // Non cluster markers
    if (!props.cluster) {
      const markerId = props.timeAwareFeatureId ?? props.featureId ?? props.id;
      let marker = map.clusterMarkers[markerId];
      if (!marker) {
        const el = document.createElement('div');
        marker = map.clusterMarkers[markerId] = new Marker({
          element: el,
        }).setLngLat(coords);

        const clusterMarkerComponent = h(UiMapMarker, {
          feature,
          marker,
        });
        render(clusterMarkerComponent, el);
      }

      newMarkers[markerId] = marker;
      if (!map.clusterMarkersOnScreen[markerId]) marker.addTo(mapbox);
      continue;
    }

    // Cluster markers
    let clusterId = props.cluster_id;
    // style changes can balk this completely, add a get out of jail free timeout
    // when there is an issue, this gets re-called anyway because the map does a rebuild
    // when the style change gets upset
    const clusterLeaves = await new Promise<GeoJSON.Feature<GeoJSON.Geometry>[]>((resolve) => {
      const timer = setTimeout(() => resolve([]), 100); // this has worked fine with 2ms, but best to give it a chance

      // note that this is actually a sync call, despite requiring the callback
      // I checked the source code
      source.getClusterLeaves(clusterId, Infinity, 0, (error, leaves) => {
        clearTimeout(timer);
        resolve(error ? [] : leaves);
      });
    });

    const sortedClusterHazards = getSortedHazardFeatures(clusterLeaves);

    if (sortedClusterHazards.length < 1) continue;

    // Compose the clusterId from it's leaves (IDs) to prevent collisions
    clusterId = await generateChecksum(
      sortedClusterHazards.map((hazard) => hazard.properties?.timeAwareFeatureId ?? hazard.properties?.featureId ?? hazard.id).join(',')
    );

    let marker = map.clusterMarkers[clusterId];

    if (!marker) {
      const el = document.createElement('div');

      marker = map.clusterMarkers[clusterId] = new Marker({
        element: el,
      }).setLngLat(coords);

      const clusterMarkerComponent = h(UiMapClusterMarker, {
        coords,
        marker,
        sortedClusterHazards,
        clusterLeaves,
        onExploded: (exploded: boolean) => {
          if (exploded) map.clustersToClean.add(clusterId);
          else map.clustersToClean.delete(clusterId);
        },
      });

      render(clusterMarkerComponent, el);
    }

    newMarkers[clusterId] = marker;
    if (!map.clusterMarkersOnScreen[clusterId]) marker.addTo(mapbox);
  }

  // Remove previously added markers that are no longer visible.
  for (const id in map.clusterMarkersOnScreen) {
    if (!newMarkers[id]) {
      if (map.clustersToClean.has(id)) {
        // unmount them and remove fully if they need clean up
        map.clustersToClean.delete(id);
        render(null, map.clusterMarkers[id].getElement()!);
        delete map.clusterMarkers[id];
      }

      map.clusterMarkersOnScreen[id].remove();
      delete map.clusterMarkersOnScreen[id];
    }
  }

  map.clusterMarkersOnScreen = newMarkers;
};
